import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CalendarDays, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { NewTripWizard } from "../../components/NewTripWizard";
import { TripCard, TripSkeleton } from "../../components/TripCard";
import { api } from "../../lib/api";
import type { Trip } from "../../lib/types";

const filters = [
  ["all", "All journeys"],
  ["upcoming", "Upcoming"],
  ["completed", "Completed"],
  ["cancelled", "Cancelled"],
] as const;

export function TripsPage() {
  const [filter, setFilter] = useState<(typeof filters)[number][0]>("all");
  const [wizard, setWizard] = useState(false);
  const { data: trips = [], isLoading, isError, refetch } = useQuery({ queryKey: ["trips"], queryFn: () => api<Trip[]>("/trips") });
  const visible = useMemo(() => trips.filter((trip) => {
    if (filter === "all") return true;
    if (filter === "upcoming") return !["completed", "cancelled", "archived"].includes(trip.status);
    return trip.status === filter;
  }), [filter, trips]);

  return <section className="trips-page page-width">
    <header className="trips-heading"><div><p className="eyebrow">MY TRAVEL</p><h1>Every journey, in one place.</h1><p>Open a plan, continue shaping it, or revisit where you have been.</p></div><button className="button primary" onClick={() => setWizard(true)}><Plus size={17} />Create a journey</button></header>
    <nav className="trip-filters" aria-label="Filter journeys">{filters.map(([key, label]) => {
      const count = key === "all" ? trips.length : key === "upcoming" ? trips.filter((trip) => !["completed", "cancelled", "archived"].includes(trip.status)).length : trips.filter((trip) => trip.status === key).length;
      return <button key={key} className={filter === key ? "active" : ""} onClick={() => setFilter(key)}>{label}<span>{count}</span></button>;
    })}</nav>
    {isError ? <div className="error-state"><p>We couldn’t load your journeys.</p><button onClick={() => refetch()}>Try again</button></div> : isLoading ? <div className="trips-grid"><TripSkeleton /><TripSkeleton /><TripSkeleton /></div> : visible.length ? <div className="trips-grid">{visible.map((trip) => <TripCard trip={trip} key={trip.id} />)}<button className="new-trip-card" onClick={() => setWizard(true)}><Plus /><strong>Create another journey</strong><span>Start with a verified destination</span></button></div> : <div className="trips-empty"><CalendarDays /><h2>No {filter === "all" ? "journeys" : filter} journeys yet.</h2><p>Your plans will appear here as their status changes.</p><button className="button primary" onClick={() => setWizard(true)}>Plan a trip<ArrowRight size={16} /></button></div>}
    {wizard && <NewTripWizard onClose={() => setWizard(false)} />}
  </section>;
}
